import { useEffect, useState } from 'react';
import { supabase, NewsPost } from '../lib/supabase';
import { ArrowLeft } from 'lucide-react';
import CategorySection from '../components/CategorySection';

interface NewsPageProps {
  onNavigateToArticle: (id: string) => void;
  onBack: () => void;
}

export default function NewsPage({ onNavigateToArticle, onBack }: NewsPageProps) {
  const [posts, setPosts] = useState<NewsPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await supabase
        .from('app_news_posts')
        .select('id,title,slug,excerpt,cover_image,published_at,author,author_image,category')
        .order('published_at', { ascending: false });

      if (fetchError) throw fetchError;
      setPosts((data || []) as NewsPost[]);
    } catch (err) {
      console.error('Error loading news posts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load news posts');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-red-500 font-horizon text-2xl animate-pulse">Loading News...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-red-500 text-xl">Error: {error}</div>
      </div>
    );
  }

  const latestNews = posts.filter(p => !p.category || p.category === 'latest-news').slice(0, 6);
  const indieGames = posts.filter(p => p.category === 'indie-games').slice(0, 6);
  const techAI = posts.filter(p => p.category === 'tech-ai').slice(0, 6);
  const reviews = posts.filter(p => p.category === 'reviews').slice(0, 6);
  const freeResources = posts.filter(p => p.category === 'free-resources').slice(0, 8);

  return (
    <div className="min-h-screen bg-black">
      <div className="fixed top-0 left-0 right-0 z-40 bg-black/95 backdrop-blur-sm border-b border-red-500/30">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-red-500 hover:text-red-400 group transition-colors"
            >
              <ArrowLeft className="group-hover:-translate-x-1 transition-transform" size={20} />
              <span className="font-bold">Back to Home</span>
            </button>
            <div className="text-red-500 font-horizon text-xs hidden md:block">
              News & Resources
            </div>
          </div>

          <nav className="mt-4 flex flex-wrap gap-4 text-sm">
            <a href="#latest" className="text-gray-400 hover:text-red-400 transition-colors font-bold">Latest</a>
            <a href="#indie" className="text-gray-400 hover:text-red-400 transition-colors font-bold">Top Indie Games</a>
            <a href="#tech" className="text-gray-400 hover:text-red-400 transition-colors font-bold">Tech & AI</a>
            <a href="#reviews" className="text-gray-400 hover:text-red-400 transition-colors font-bold">Game Reviews</a>
            <a href="#free" className="text-gray-400 hover:text-red-400 transition-colors font-bold">Free Resources</a>
          </nav>
        </div>
      </div>

      <div className="pt-32 pb-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h1 className="font-horizon text-5xl md:text-7xl text-red-500 mb-4 animate-fadeIn">
              Latest News & Blog
            </h1>
            <p className="text-gray-400 text-lg animate-fadeIn animation-delay-200">
              Your source for indie game news, development insights, and free resources
            </p>
          </div>

          <div id="latest">
            <CategorySection
              title="Latest News"
              posts={latestNews}
              onNavigateToArticle={onNavigateToArticle}
              layout="featured"
            />
          </div>

          <div id="indie">
            <CategorySection
              title="Top Indie Games"
              posts={indieGames}
              onNavigateToArticle={onNavigateToArticle}
              layout="grid"
            />
          </div>

          <div id="tech">
            <CategorySection
              title="Tech & AI"
              posts={techAI}
              onNavigateToArticle={onNavigateToArticle}
              layout="list"
            />
          </div>

          <div id="reviews">
            <CategorySection
              title="Game Reviews"
              posts={reviews}
              onNavigateToArticle={onNavigateToArticle}
              layout="grid"
            />
          </div>

          <div id="free">
            <CategorySection
              title="FREE How to Make A Viral Game"
              posts={freeResources}
              onNavigateToArticle={onNavigateToArticle}
              layout="list"
            />
          </div>

          {posts.length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-400 text-xl">No content available yet. Check back soon!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
